import { alpha, Box, Button, CircularProgress, Stack, TextField, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { keyframes } from '@emotion/react'
import WidgetGrid from './Widgets/base/WidgetGrid'
import { apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import { useParams } from 'react-router-dom'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import AlertUI from '../generic/AlertUI'
import useMosInfo from './Widgets/common/provider/useMosInfo'
import { getWidgetProviderForType } from './Widgets/base/WidgetProvider'
import { QualityIncidenceWidget } from './Widgets/qualityIncidences/QualityIncidenceWidgetProvider'
import WidgetTemplateManagerDialog from './widgetTemplates/WidgetTemplateManagerDialog'
import InitialScreenTemplateManagerDialog from './initialScreenTemplates/InitialScreenTemplateManagerDialog'
import { parseTemplateWidgets } from './widgetTemplates/widgetTemplateParse'
import { serializeTemplateWidgets } from './widgetTemplates/widgetTemplateUtils'
import { isSingleJobModeEnabled } from './Widgets/common/singleJobMode'

const MosScreen = () => {
  const [widgets, setWidgets] = useState([])
  const [reloadWidgets, setReloadWidgets] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [editing, setEditing] = useState(false)
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false)
  // When set, the grid is editing this template's layout (not the device's).
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [editingTemplateName, setEditingTemplateName] = useState('')

  const urlParams = useParams()

  const deviceId = useMemo(() => urlParams && urlParams.deviceId ? parseInt(urlParams.deviceId) : null, [urlParams])

  const { device, setWidgetsLoaded, setTargetDeviceId, widgetsLoaded, globalData, setGlobalData } = useMosInfo()
  const theme = useTheme()

  const isUserAuthorized = useAuthorization()

  const hasQualityIncidenceWidget = useMemo(
    () => widgets.some((widget) => widget.type === 'QualityIncidenceWidgetProvider'),
    [widgets]
  )

  // Single Job Mode is configured on the Operation widget but binds the whole work centre: Planned
  // Jobs, its "Show all" modal and the job detail gate their Start/Join on it. `WidgetGrid.setData`
  // writes into this same `widgets` state, so toggling the switch republishes it at once, whatever
  // the mount order of the widgets. `false` when the layout has no Operation widget.
  const singleJobMode = useMemo(() => isSingleJobModeEnabled(widgets), [widgets])

  useEffect(() => {
    setGlobalData((prev) => (prev?.singleJobMode === singleJobMode ? prev : { ...prev, singleJobMode }))
  }, [singleJobMode, setGlobalData])

  const blinkAnimation = useMemo(() => keyframes`
  0% {
    box-shadow: inset 0 0 0 4px ${alpha(theme.palette.error.main, 0)};
  }
  50% {
    box-shadow: inset 0 0 20px 10px ${alpha(theme.palette.error.main, 0.6)};
  }
  100% {
    box-shadow: inset 0 0 0 4px ${alpha(theme.palette.error.main, 0)};
  }
`, [theme])

  useEffect(() => {
    setTargetDeviceId(deviceId)
    setGlobalData((prev) => ({ ...prev, incidence: null }))
  }, [setTargetDeviceId, deviceId, setGlobalData])

  useEffect(() => {
    // Load the widgets
    let cancelled = false
    const loadWidgets = async () => {
      setLoading(true)
      setError(null)
      const url = 'widgets/' + (device ? 'device/' + device.id : 'initialScreen')
      try {
        const result = await apiGet(url)
        const newWidgets = (Array.isArray(result) ? result : [])
          .map((raw) => {
            const element = { ...raw }
            let parsed = null
            let invalid = false

            if (element.data) {
              if (typeof element.data === 'string') {
                try {
                  parsed = JSON.parse(element.data)
                } catch (err) {
                  invalid = true
                }
              } else {
                parsed = element.data
              }
            }

            const provider = getWidgetProviderForType(element.type)
            if (invalid) {
              const defaultConf = provider.getDefaultConfiguration ? provider.getDefaultConfiguration() : null
              element.data = defaultConf || null
              element.invalidConfig = true
              console.error('Widget configuration parse error', { deviceId, widgetId: element.id, type: element.type, raw: element.data })
            } else {
              element.data = parsed
            }

            const info = provider.getWidgetInfo()

            element.id = element.id !== undefined && element.id !== null ? String(element.id) : element.id

            const toNumberOrUndefined = (value) => {
              if (value === null || value === undefined || value === '') return undefined
              const n = typeof value === 'number' ? value : Number(value)
              return Number.isFinite(n) ? n : undefined
            }

            element.x = toNumberOrUndefined(element.x)
            element.y = toNumberOrUndefined(element.y)
            element.w = toNumberOrUndefined(element.w) ?? info.defaultW
            element.h = toNumberOrUndefined(element.h) ?? info.defaultH
            element.minW = toNumberOrUndefined(element.minW)
            element.minH = toNumberOrUndefined(element.minH)
            element.maxW = toNumberOrUndefined(element.maxW)
            element.maxH = toNumberOrUndefined(element.maxH)

            return element
          })
          .filter(Boolean)
          .sort((a, b) => {
            const ay = a.y ?? Number.MAX_SAFE_INTEGER
            const by = b.y ?? Number.MAX_SAFE_INTEGER
            if (ay !== by) return ay - by
            const ax = a.x ?? Number.MAX_SAFE_INTEGER
            const bx = b.x ?? Number.MAX_SAFE_INTEGER
            if (ax !== bx) return ax - bx
            const aid = a.id ?? ''
            const bid = b.id ?? ''
            return String(aid).localeCompare(String(bid))
          })
        if (!cancelled) {
          setWidgets(newWidgets)
          setWidgetsLoaded(true)
          setLoading(false)
        }
      } catch (e) {
        if (!cancelled) {
          setError(e)
          setWidgetsLoaded(true)
          setLoading(false)
          showAlert({
            title: t_('error loading widgets'),
            message: t_(e.message || 'Unknown error'),
            severity: 'error'
          })
        }
      }
    }
    loadWidgets()
    return () => { cancelled = true }
  }, [reloadWidgets, device, setWidgetsLoaded, deviceId, showAlert, t_])

  const handleRetry = useCallback(() => {
    setError(null)
    setLoading(true)
    setReloadWidgets(!reloadWidgets)
  }, [reloadWidgets])

  const exitTemplateEdit = useCallback(() => {
    setEditingTemplate(null)
    setEditingTemplateName('')
    setEditing(false)
    // Restore the device/initial layout that was replaced while editing.
    setReloadWidgets((prev) => !prev)
  }, [])

  // endpointBase selects the catalog being edited: the device templates (default)
  // or the initial-screen templates. It travels inside editingTemplate so onSave
  // writes back to the same catalog.
  const enterTemplateEdit = useCallback(async (template, endpointBase = 'widgets/templates') => {
    try {
      const full = await apiGet(endpointBase + '/' + template.id)
      setWidgets(parseTemplateWidgets(full?.widgets))
      setEditingTemplate({ id: full?.id ?? template.id, name: full?.name ?? template.name, endpointBase })
      setEditingTemplateName(full?.name ?? template.name ?? '')
      setEditing(true)
      setTemplatesDialogOpen(false)
    } catch (e) {
      showAlert({ title: t_('error loading widget template'), message: t_(e.message || 'Unknown error'), severity: 'error' })
    }
  }, [showAlert, t_])

  const onSave = useCallback(async () => {
    if (editingTemplate) {
      const name = editingTemplateName.trim()
      if (!name) {
        showAlert({ title: t_('A name is required'), message: '', severity: 'error' })
        return
      }
      try {
        await apiPut((editingTemplate.endpointBase ?? 'widgets/templates') + '/' + editingTemplate.id, { id: editingTemplate.id, name, widgets: serializeTemplateWidgets(widgets) })
        showAlert({ title: t_('Template saved'), message: '', severity: 'success' })
        exitTemplateEdit()
      } catch (e) {
        showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
      }
      return
    }
    const url = 'widgets/' + (deviceId ? 'device/' + deviceId : 'initialScreen')
    // Story 213 v2: `sensitive` is Sun's reading of the layout (C1c), not part of it; it does not travel back.
    await apiPost(url, widgets.map(({ sensitive, ...item }) => { return { ...item, data: item.data ? JSON.stringify(item.data) : null } }))
      .then(() => {
        showAlert({ title: t_('Saved correctly'), severity: 'success', message: '' })
        setEditing(false)
      })
      .catch((e) => {
        showAlert({
          title: '',
          message: t_(e.message),
          severity: 'error'
        })
      })
  }, [editingTemplate, editingTemplateName, widgets, deviceId, showAlert, t_, exitTemplateEdit])

  if (!widgetsLoaded || loading) {
    return <Stack justifyContent='space-around' alignItems='center' sx={{ height: '100%' }}><CircularProgress size='10rem' /></Stack>
  }

  if (error) {
    return (
      <Stack justifyContent='center' alignItems='center' sx={{ height: '100%', gap: 2 }}>
        <Typography variant='h6'>{t_('error loading widgets')}</Typography>
        <Button variant='contained' onClick={handleRetry}>{t_('Retry')}</Button>
      </Stack>
    )
  }
  if (widgets.length === 0 && !isUserAuthorized('MOS_EDIT')) {
    return <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Typography variant='h3'>{t_('This screen has not been configured yet')}</Typography></div>
  }

  return (
    <Box sx={{
      height: '100%',
      background: theme.palette.background.default,
      animation: globalData?.incidence && device ? `${blinkAnimation} 2s infinite` : 'none'
    }}
    >
      <div style={{ position: 'sticky', left: '1rem', right: '1rem', top: '1rem', paddingBottom: '1rem', zIndex: 100 }}>{AlertElement}</div>
      {editingTemplate && (
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={{
            position: 'sticky',
            top: '1rem',
            zIndex: 100,
            mx: '1rem',
            mb: '1rem',
            p: 1.5,
            borderRadius: 1,
            border: `1px solid ${theme.palette.info.main}`,
            background: alpha(theme.palette.info.main, 0.12)
          }}
        >
          <Typography sx={{ whiteSpace: 'nowrap' }}>{t_('Editing template')}:</Typography>
          <TextField
            size='small'
            label={t_('Name')}
            value={editingTemplateName}
            onChange={(event) => setEditingTemplateName(event.target.value)}
            sx={{ minWidth: '16rem' }}
            inputProps={{ maxLength: 255 }}
          />
          <Typography variant='caption' color='text.secondary' sx={{ flexGrow: 1 }}>
            {t_('Save to update this template')}
          </Typography>
          <Button variant='outlined' color='inherit' onClick={exitTemplateEdit}>{t_('Cancel')}</Button>
        </Stack>
      )}
      {widgets
        ? <WidgetGrid
            widgets={widgets}
            setWidgets={setWidgets}
            reloadWidgets={() => { setReloadWidgets(!reloadWidgets) }}
            onSave={onSave}
            editing={editing}
            setEditing={setEditing}
            onTemplatesClick={editingTemplate ? undefined : () => setTemplatesDialogOpen(true)}
          />
        : null}
      {deviceId
        ? (
          <WidgetTemplateManagerDialog
            open={templatesDialogOpen}
            setOpen={setTemplatesDialogOpen}
            deviceId={deviceId}
            widgets={widgets}
            onApplied={() => { setReloadWidgets((prev) => !prev) }}
            onEditTemplate={enterTemplateEdit}
          />
          )
        : (
          <InitialScreenTemplateManagerDialog
            open={templatesDialogOpen}
            setOpen={setTemplatesDialogOpen}
            widgets={widgets}
            onApplied={() => { setReloadWidgets((prev) => !prev) }}
            onEditTemplate={(template) => enterTemplateEdit(template, 'widgets/initialScreenTemplates')}
          />
          )}
      {!hasQualityIncidenceWidget && <QualityIncidenceWidget hideTrigger />}
    </Box>
  )
}

export default MosScreen
